"use client"
import React, { useEffect, useRef } from 'react'
import { useTransitionRouter } from "next-view-transitions"
import gsap from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import { SplitText } from "gsap/SplitText"
import Image from 'next/image'
import { InteractiveHoverButton } from "@/components/magicui/interactive-hover-button"
import { useRouter } from "next/navigation"
import { slideInOut } from '@/app/lib/gsapAnimation'
import TextSplit from '../ui/TextSplit'


gsap.registerPlugin(ScrollTrigger)

export default function AgencyPresentation() {

  const transitionRouter = useTransitionRouter()
  const router =  useRouter()
  const sectionRef = useRef(null)

  const handleClick = (e: React.MouseEvent, url: string) => {
    e.preventDefault()
  
    // Blur the currently focused element
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }

    setTimeout(() => {
      if (!document.startViewTransition) {
        router.push(url)
        return
      }

      transitionRouter.push(url, { onTransitionReady: slideInOut })
      return
    }, 300)

  }


  useEffect(() => {
    if (!sectionRef.current) return

    const section = sectionRef.current
    const bg = section.querySelector(".bg-gradient")
    const title = section.querySelector("h2")
    const image = section.querySelector(".reveal-image")
    const paragraphs = section.querySelectorAll("p")

    // Split the title into characters
    const split = new SplitText(title, {
      type: "lines, words",
      autoSplit: true,
      mask: "lines"
    })

    const agencytimeline = gsap.timeline({
      scrollTrigger: {
        trigger: title,
        start: "top 60%",
        toggleActions: "play none none none"
      }
    })

    agencytimeline
      .from(split.lines, {
        opacity: 0,
        y: 50,
        duration: 0.5,
        stagger: 0.1,
        ease: "power3.out"
      })
      .from(
        split.words,
        {
          y: 100,
          opacity: 0,
          stagger: 0.015,
          ease: "power3.out"
        },
        "-=0.4"
      )

    // Reveal image with polygon mask
    gsap.fromTo(
      image,
      {
        clipPath: "polygon(0 100%, 100% 100%, 100% 100%, 0 100%)",
        opacity: 0
      },
      {
        clipPath: "polygon(0 0%, 100% 0%, 100% 100%, 0 100%)",
        opacity: 1,
        duration: 1,
        delay: 1,
        ease: "power2.out",
        pointerEvents: "none",
        scrollTrigger: {
          trigger: image,
          start: "top 95%"
        }
      }
    )

    // Animate gradient background change
    gsap.to(bg, {
      backgroundColor: "linear-gradient(90deg,rgba(42, 123, 155, 1) 0%, rgba(87, 199, 133, 1) 50%, rgba(237, 221, 83, 1) 100%);",
      duration: 0.5,
      ease: "none",
      scrollTrigger: {
        trigger: section,
        start: "center center",
        end: "bottom bottom",
        scrub: true
      }
    })

    // Ensure everything is recalculated
    setTimeout(() => ScrollTrigger.refresh(), 0)

    return () => {
      // Clean up on unmount
      ScrollTrigger.getAll().forEach(trigger => trigger.kill())
      gsap.killTweensOf([title, image, paragraphs, bg])
      split.revert()
    }
  }, [])


  return (
    <section className="container mx-auto pt-24 lg:pt-32 bg-gradient" ref={sectionRef}>
      <div className="overflow-hidden relative isolate clip-heading-mask max-w-screen-lg mx-auto">
        <h2 className="text-center leading-tight">A full-service agency with strategic branding and UX expertise</h2>
      </div>
      <div className="grid grid-cols-12 items-center gap-x-4 gap-y-4 lg:gap-x-20 py-24">
        <div className="order-1 lg:order-0 col-span-12 lg:col-span-5">
          <TextSplit animateOnScroll={true} delay={0.05}>
            <p>For dynamic small and medium businesses in France and Europe looking to command their market, we’re the only compact, flexible, and multilingual group of branding experts with big-agency capabilities.</p>
            <p>We love helping our clients truly differentiate—so they’re chosen by their ideal customers, not compared.</p>
          </TextSplit>
          <div className="flex gap-4 mt-6" onClick={e => handleClick(e, "/about")}>
            <InteractiveHoverButton>About us</InteractiveHoverButton>
          </div>
        </div>
        <div className="order-0 lg:order-1 col-span-12 lg:col-span-6 h-full flex justify-center">
          <figure className="relative aspect-square lg:aspect-video reveal-image w-full h-full overflow-hidden rounded-3xl max-h-[400px] max-w-[440px]">
            <Image src="/img/MYS_Lifestyle.jpg" alt="placeholder" className="object-cover w-full h-full" fill />
          </figure>
        </div>
        <div className="lg:col-span-1"></div>
      </div>
    </section>
  )
}
